// Sprint 6 (Phase 2, D48) — Render-time aggregation.
//
// A typical agent turn fires bursts of homogeneous tool calls (e.g. 12 `Read`s
// while exploring a directory, 8 `Edit`s while applying a refactor). Rendering
// each as its own card produces noise, not progress — the founder sees 23
// rows when one summary would suffice.
//
// `aggregateMessages` is a pure render-time pass that walks the topLevel
// message stream and collapses 3+ consecutive same-tool `tool_use` events
// into an `AggregationItem`. The original messages are preserved inside the
// item so the developer-mode reveal can drill into each one.
//
// Threshold and behavior decisions taken by Santi (do not reopen):
// - Threshold: 3+ consecutive same-tool calls within a turn.
// - Subagent tool calls (`Task`, `Agent`) are NEVER aggregated — they have
//   their own `SubagentPanel` and aggregating them would hide the subagent
//   shell entirely.
// - Aggregation is broken by ANY non-`tool_use` message (assistant_text,
//   thinking_block, system_event, unpaired tool_result, etc.). A new
//   assistant turn naturally resets via the assistant_text/thinking_block
//   between turns.
// - Aggregation is also broken by a different tool name.

import type { Message } from '../../types';
import { asToolUse, SUBAGENT_TOOL_NAMES } from '../messages';
import { longestCommonDir } from './path-utils';

/** 3+ same-tool consecutive in a turn → aggregate. */
export const AGGREGATION_THRESHOLD = 3;

export type AggregationToolKind =
  | 'Read'
  | 'Grep'
  | 'Glob'
  | 'Edit'
  | 'Write'
  | 'MultiEdit'
  | 'Bash'
  | 'WebFetch'
  | 'WebSearch'
  | 'NotebookEdit'
  | 'TodoWrite'
  | 'Skill'
  | 'PushNotification'
  | string;

export interface AggregationItem {
  kind: 'aggregation';
  /** Stable key for React reconciliation (id of first item). */
  key: string;
  /** Tool name shared by all items, e.g. `Read`. */
  toolName: AggregationToolKind;
  /** Headline already humanized: `Explored 12 files in src/auth`. */
  headline: string;
  /** Number of aggregated tool_use events. */
  count: number;
  /** Number of tool_use events that have an error tool_result paired. */
  errorCount: number;
  /** Number of tool_use events that don't yet have a tool_result. */
  runningCount: number;
  /** Original tool_use Messages, in order. */
  items: Message[];
}

export interface SingleItem {
  kind: 'single';
  message: Message;
}

export type RenderItem = SingleItem | AggregationItem;

export interface AggregateInput {
  topLevel: Message[];
  /** Map from tool_use_id → its tool_result Message, when present. */
  resultByToolUseId: Map<string, Message>;
}

/** Public entry point. Pure. */
export function aggregateMessages(input: AggregateInput): RenderItem[] {
  const out: RenderItem[] = [];
  let run: Message[] = [];
  let runTool: string | null = null;

  const flush = () => {
    if (run.length === 0) return;
    if (run.length >= AGGREGATION_THRESHOLD && runTool && !SUBAGENT_TOOL_NAMES.has(runTool)) {
      out.push(buildAggregation(run, runTool, input.resultByToolUseId));
    } else {
      for (const m of run) out.push({ kind: 'single', message: m });
    }
    run = [];
    runTool = null;
  };

  for (const m of input.topLevel) {
    if (m.type !== 'tool_use') {
      flush();
      out.push({ kind: 'single', message: m });
      continue;
    }
    const tu = asToolUse(m);
    if (SUBAGENT_TOOL_NAMES.has(tu.name)) {
      // Never aggregate subagent shells; they own their own visual block.
      flush();
      out.push({ kind: 'single', message: m });
      continue;
    }
    if (runTool === null) {
      runTool = tu.name;
      run.push(m);
      continue;
    }
    if (runTool === tu.name) {
      run.push(m);
      continue;
    }
    // Different tool name — flush previous run, start a new one.
    flush();
    runTool = tu.name;
    run.push(m);
  }
  flush();
  return out;
}

function buildAggregation(
  items: Message[],
  toolName: string,
  resultByToolUseId: Map<string, Message>,
): AggregationItem {
  let errorCount = 0;
  let runningCount = 0;
  const filePaths: string[] = [];
  for (const m of items) {
    const tu = asToolUse(m);
    const tr = resultByToolUseId.get(tu.tool_use_id);
    if (!tr) {
      runningCount += 1;
    } else {
      const isErr = (() => {
        const c = tr.content as { is_error?: unknown } | null;
        return !!c && c.is_error === true;
      })();
      if (isErr) errorCount += 1;
    }
    const fp = readFilePathLike(tu.input);
    if (fp) filePaths.push(fp);
  }
  const commonDir = filePaths.length > 0 ? longestCommonDir(filePaths) : null;
  return {
    kind: 'aggregation',
    key: `agg-${items[0]?.id ?? 'x'}`,
    toolName,
    headline: aggregationHeadline(toolName, items.length, commonDir),
    count: items.length,
    errorCount,
    runningCount,
    items,
  };
}

function readFilePathLike(input: Record<string, unknown>): string | null {
  for (const key of ['file_path', 'path', 'notebook_path']) {
    const v = input[key];
    if (typeof v === 'string' && v.length > 0) return v;
  }
  // Glob inputs use `pattern`; we treat patterns as paths for the common-dir
  // calculation only when they look path-like.
  const pattern = input.pattern;
  if (typeof pattern === 'string' && pattern.includes('/')) return pattern;
  return null;
}

export function aggregationHeadline(
  toolName: string,
  count: number,
  commonDir: string | null,
): string {
  const inDir = commonDir ? ` in ${commonDir}` : '';
  switch (toolName) {
    case 'Read':
      return `Explored ${count} files${inDir || ' across files'}`;
    case 'Grep':
      return `Searched ${count} times`;
    case 'Glob':
      return `Listed ${count} sets of files`;
    case 'Edit':
    case 'MultiEdit':
    case 'Write':
      return `Modified ${count} files${inDir}`;
    case 'Bash':
      return `Ran ${count} commands`;
    case 'WebFetch':
    case 'WebSearch':
      return `Visited ${count} pages`;
    case 'NotebookEdit':
      return `Edited ${count} notebooks${inDir}`;
    case 'TodoWrite':
      return `Updated plan ${count} times`;
    default: {
      const titled = toolName.replace(/([A-Z])/g, ' $1').trim();
      return `Ran ${count} ${titled} calls`;
    }
  }
}
